import { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export interface FonepayQrSession {
  prn: string;
  qrImage: string;
  expiresAt: string;
  mock: boolean;
}

export interface FonepayQrStatusResponse {
  status: 'pending' | 'completed' | 'failed' | 'expired';
  message?: string;
  reference?: string;
  mock?: boolean;
}

export interface FonepayQrPaymentProps {
  open: boolean;
  amount: number;
  session: FonepayQrSession | null;
  onClose: () => void;
  onRetry: () => void;
  checkStatus: (prn: string) => Promise<FonepayQrStatusResponse>;
  onCompleted: (mock: boolean) => void;
}

function remainingSeconds(expiresAt?: string): number {
  if (!expiresAt) return 0;
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 1000));
}

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export default function FonepayQrPayment({
  open,
  amount,
  session,
  onClose,
  onRetry,
  checkStatus,
  onCompleted,
}: FonepayQrPaymentProps) {
  const [secondsLeft, setSecondsLeft] = useState(() => remainingSeconds(session?.expiresAt));
  const [status, setStatus] = useState<FonepayQrStatusResponse['status']>('pending');
  const [message, setMessage] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const completedRef = useRef(false);
  const formattedAmount = useMemo(
    () => new Intl.NumberFormat('en-NP', { maximumFractionDigits: 2 }).format(amount),
    [amount],
  );

  useEffect(() => {
    setSecondsLeft(remainingSeconds(session?.expiresAt));
    setStatus('pending');
    setMessage(null);
    setChecking(false);
    completedRef.current = false;
  }, [session?.prn, session?.expiresAt]);

  useEffect(() => {
    if (!open || !session || status !== 'pending') return;
    const timer = window.setInterval(() => {
      const remaining = remainingSeconds(session.expiresAt);
      setSecondsLeft(remaining);
      if (remaining === 0) {
        setStatus('expired');
        setMessage('This QR code has expired. Generate a new one to try again.');
      }
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [open, session, status]);

  useEffect(() => {
    if (!open || !session || status !== 'pending') return;
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      setChecking(true);
      try {
        const result = await checkStatus(session.prn);
        if (cancelled) return;
        setStatus(result.status);
        setMessage(result.message ?? null);
        if (result.status === 'completed' && !completedRef.current) {
          completedRef.current = true;
          onCompleted(Boolean(result.mock ?? session.mock));
          return;
        }
        if (result.status === 'pending') {
          timer = window.setTimeout(poll, 2_500);
        }
      } catch {
        if (!cancelled) {
          setMessage('Could not check payment yet. Retrying automatically…');
          timer = window.setTimeout(poll, 4_000);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    timer = window.setTimeout(poll, 1_500);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [checkStatus, onCompleted, open, session, status]);

  const canRetry = status === 'failed' || status === 'expired';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Scan and pay with Fonepay</DialogTitle>
      <DialogContent>
        {session ? (
          <Stack spacing={2} sx={{ alignItems: 'center', pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Scan this dynamic QR using a Fonepay-supported mobile banking or wallet app. The
              amount is already included.
            </Typography>
            <Box
              component="img"
              src={session.qrImage}
              alt={`Fonepay QR for NPR ${formattedAmount}`}
              sx={{
                width: 260,
                maxWidth: '100%',
                aspectRatio: '1 / 1',
                objectFit: 'contain',
                bgcolor: '#fff',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1,
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              NPR {formattedAmount}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              {status === 'pending' && checking && <CircularProgress size={16} />}
              <Typography
                variant="body2"
                color={status === 'pending' ? 'text.secondary' : 'error.main'}
              >
                {status === 'pending'
                  ? `Waiting for payment · ${formatTime(secondsLeft)} remaining`
                  : message || 'Payment was not completed'}
              </Typography>
            </Stack>
            {message && status === 'pending' && <Alert severity="info">{message}</Alert>}
            {session.mock && (
              <Alert severity="info">
                Mock mode: payment will verify automatically after a few seconds. No money is
                transferred.
              </Alert>
            )}
          </Stack>
        ) : (
          <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {canRetry && (
          <Button variant="contained" onClick={onRetry}>
            Generate new QR
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
